/** @jsx jsx */
import { Card, CardBody, customColors, customColorsRaw } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import { useMemo } from 'react';

import { InsightsLineChartTooltip } from 'src/app/graph/insights/InsightsLineChartTooltip';
import { StackedTimeSeriesData } from 'src/components/charts/helpers/dataTypes';
import {
  LineChart,
  LineChartStackConfig,
} from 'src/components/charts/LineChart';
import { format } from 'src/lib/format';
import { Resolution } from 'src/lib/graphqlTypes/types';

export function ErroredRequestsPercentChart({
  data,
  loading,
  resolution,
}: {
  data: StackedTimeSeriesData[];
  loading: boolean;
  resolution: Resolution;
}) {
  const lineChartStacks = useMemo(
    (): LineChartStackConfig<StackedTimeSeriesData>[] => [
      {
        colorGradientTop: customColors.bg['error-selected'],
        colorGradientBottom: `rgb(${customColorsRaw.bg['error-selected']} / 0)`,
        primaryColor: customColors.border.error,
        cursorColor: customColors.brand.accent,
        label: 'Errored request %',
        data: data ?? [],
        getValue: ({ timestamp, stackCounts }: StackedTimeSeriesData) => {
          const erroredRequests = stackCounts[0]?.value ?? 0;
          const denominator = stackCounts[1]?.value ?? 0;
          return {
            timestamp,
            value: denominator > 0 ? erroredRequests / denominator : 0,
          };
        },
      },
    ],
    [data],
  );

  if (!data) return <div>Loading...</div>;

  return (
    <Card variant="unstyled" className="p-4">
      <CardBody>
        <div className="flex">
          <div className="flex-1">
            <div
              className="mr-2 inline-block size-3 rounded-full"
              css={css`
                background-color: rgb(${customColorsRaw.border.error} / 0.8);
              `}
            />
            <span>Errored request %</span>
          </div>
        </div>
        <LineChart
          stacks={lineChartStacks}
          stacked
          strokeWidth={1}
          className={classnames('h-60 pl-[15px]', { 'opacity-40': loading })}
          resolution={resolution}
          formatValue={(_, value) => format.percent(value)}
          tooltipLabelAs={<InsightsLineChartTooltip />}
          hideCursorArrow
        />
      </CardBody>
    </Card>
  );
}
